import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { CARD } from './styles';
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Area, AreaChart, Bar, BarChart, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from 'recharts';
/**
 * Chart chrome drawn from the theme. The theme's palette mains are tuned for buttons on a light
 * ground and several render as near-black navy on the dark shell, so the series colours are picked
 * for contrast against both grounds, keeping Livable's teal as the lead.
 */
export function useChartTheme() {
  const theme = useTheme();
  const series = theme.palette.mode === 'dark' ? ['#2ec4b6', '#5aa9e6', '#f4a261', '#c792ea', '#e56b6f'] : ['#0f8b7e', '#2f6fb0', '#c9761f', '#7b52ab', '#c2454a'];
  const axis = theme.palette.text.secondary;
  const grid = theme.palette.divider;
  const tooltipProps = {
    cursor: {
      fill: theme.palette.action.hover
    },
    contentStyle: {
      background: theme.palette.background.paper,
      border: "1px solid ".concat(grid),
      borderRadius: 8,
      fontSize: 12,
      boxShadow: theme.shadows[3]
    },
    labelStyle: {
      color: axis,
      fontSize: 11
    },
    itemStyle: {
      color: theme.palette.text.primary
    }
  };
  return {
    series,
    axis,
    grid,
    tooltipProps
  };
}

/**
 * A chart here is a reading aid inside an answer, not a dashboard tile. Recharts' defaults, heavy
 * gridlines, boxed legends, a white tooltip card, fight the surrounding text, so nearly all of the
 * chrome is turned off and what remains is drawn from the theme.
 */
/**
 * Up to six bars each keep a label, cut to what fits (the tooltip has the full name); past that, recharts skips
 * labels as it does on the line chart, so twelve months never collide.
 */
export function barAxisTicks(count) {
  const every = count <= 6;
  const max = Math.floor(60 / Math.max(1, count));
  return {
    interval: every ? 0 : 'preserveStartEnd',
    format: label => every && label.length > max ? "".concat(label.slice(0, max - 1), "\u2026") : label
  };
}
export function LivyChart(_ref) {
  let spec = _ref.spec;
  const type = spec.type,
    title = spec.title,
    unit = spec.unit,
    data = spec.data,
    reference = spec.reference;
  const _useChartTheme = useChartTheme(),
    series = _useChartTheme.series,
    axis = _useChartTheme.axis,
    grid = _useChartTheme.grid,
    tooltipProps = _useChartTheme.tooltipProps;
  const fmt = v => "".concat(unit === '$' ? '$' : '').concat(v.toLocaleString()).concat(unit && unit !== '$' ? " ".concat(unit) : '');
  const total = data.reduce((sum, d) => sum + d.value, 0);

  // Counts are integers; a y-axis reading 0.75 / 1.5 / 2.25 for "3 properties" is nonsense.
  const wholeNumbers = data.every(d => Number.isInteger(d.value));
  const axisProps = {
    tick: {
      fill: axis,
      fontSize: 11
    },
    tickLine: false,
    axisLine: {
      stroke: grid
    }
  };
  // A percentage axis starts at 0 and reaches at least the reference, so 92% of target never fills the chart.
  const top = Math.max(...data.map(d => d.value), reference !== null && reference !== void 0 ? reference : 0);
  const yAxisProps = _objectSpread(_objectSpread({}, axisProps), {}, {
    width: 48,
    allowDecimals: !wholeNumbers
  }, unit === '%' ? {
    domain: [0, Math.ceil(top / 10) * 10]
  } : {});
  const target = reference === undefined ? null : /*#__PURE__*/React.createElement(ReferenceLine, {
    y: reference,
    stroke: axis,
    strokeDasharray: "4 4",
    ifOverflow: "extendDomain"
  });
  const tooltip = /*#__PURE__*/React.createElement(Tooltip, _extends({}, tooltipProps, {
    formatter: v => [fmt(v), '']
  }));
  const barTicks = barAxisTicks(data.length);
  return /*#__PURE__*/React.createElement(Box, {
    sx: _objectSpread(_objectSpread({}, CARD), {}, {
      mt: '14px',
      p: '14px 14px 6px',
      bgcolor: 'background.paper'
    })
  }, title && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: '.04em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: '10px'
    }
  }, title), /*#__PURE__*/React.createElement(Box, {
    sx: {
      height: 200,
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement(ResponsiveContainer, {
    width: "100%",
    height: "100%"
  }, type === 'donut' ? /*#__PURE__*/React.createElement(PieChart, null, /*#__PURE__*/React.createElement(Pie, {
    data: data,
    dataKey: "value",
    nameKey: "label",
    innerRadius: "58%",
    outerRadius: "82%",
    paddingAngle: data.length > 1 ? 2 : 0,
    stroke: "none"
  }, data.map((d, i) => /*#__PURE__*/React.createElement(Cell, {
    key: d.label,
    fill: series[i % series.length]
  }))), tooltip) : type === 'line' ? /*#__PURE__*/React.createElement(LineChart, {
    data: data,
    margin: {
      top: 4,
      right: 8,
      bottom: 0,
      left: -18
    }
  }, /*#__PURE__*/React.createElement(XAxis, _extends({
    dataKey: "label"
  }, axisProps)), /*#__PURE__*/React.createElement(YAxis, yAxisProps), tooltip, target, /*#__PURE__*/React.createElement(Line, {
    type: "monotone",
    dataKey: "value",
    stroke: series[0],
    strokeWidth: 2,
    dot: false
  })) : type === 'area' ? /*#__PURE__*/React.createElement(AreaChart, {
    data: data,
    margin: {
      top: 4,
      right: 8,
      bottom: 0,
      left: -18
    }
  }, /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
    id: "livyArea",
    x1: "0",
    y1: "0",
    x2: "0",
    y2: "1"
  }, /*#__PURE__*/React.createElement("stop", {
    offset: "0%",
    stopColor: series[0],
    stopOpacity: 0.35
  }), /*#__PURE__*/React.createElement("stop", {
    offset: "100%",
    stopColor: series[0],
    stopOpacity: 0
  }))), /*#__PURE__*/React.createElement(XAxis, _extends({
    dataKey: "label"
  }, axisProps)), /*#__PURE__*/React.createElement(YAxis, yAxisProps), tooltip, target, /*#__PURE__*/React.createElement(Area, {
    type: "monotone",
    dataKey: "value",
    stroke: series[0],
    strokeWidth: 2,
    fill: "url(#livyArea)"
  })) : /*#__PURE__*/React.createElement(BarChart, {
    data: data,
    margin: {
      top: 4,
      right: 8,
      bottom: 0,
      left: -18
    }
  }, /*#__PURE__*/React.createElement(XAxis, _extends({
    dataKey: "label"
  }, axisProps, {
    interval: barTicks.interval,
    tickFormatter: barTicks.format
  })), /*#__PURE__*/React.createElement(YAxis, yAxisProps), tooltip, target, /*#__PURE__*/React.createElement(Bar, {
    dataKey: "value",
    radius: [5, 5, 0, 0],
    maxBarSize: 44
  }, data.map((d, i) => /*#__PURE__*/React.createElement(Cell, {
    key: d.label,
    fill: series[i % series.length]
  }))))), type === 'donut' && /*#__PURE__*/React.createElement(Box, {
    sx: {
      position: 'absolute',
      inset: 0,
      display: 'grid',
      placeItems: 'center',
      pointerEvents: 'none'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 22,
      fontWeight: 600,
      fontVariantNumeric: 'tabular-nums',
      lineHeight: 1.1
    }
  }, fmt(total)), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 10.5,
      color: 'text.secondary',
      letterSpacing: '.06em',
      textTransform: 'uppercase'
    }
  }, "Total")))), type === 'donut' && /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px 14px',
      mt: '10px',
      pb: '6px'
    }
  }, data.map((d, i) => /*#__PURE__*/React.createElement(Box, {
    key: d.label,
    sx: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 8,
      height: 8,
      borderRadius: '2px',
      bgcolor: series[i % series.length]
    }
  }), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11.5,
      color: 'text.secondary'
    }
  }, d.label, /*#__PURE__*/React.createElement(Box, {
    component: "span",
    sx: {
      color: 'text.primary',
      ml: '5px',
      fontVariantNumeric: 'tabular-nums'
    }
  }, fmt(d.value)))))));
}